/**
 * Copy en español + flag `isRetryable` para los errorCodes del aprobador
 * masivo de comité (Cementos LEGACY MVP).
 *
 * Consumido por `src/organisms/Committee/BulkActions/CommitteeBulkResultModal.tsx`
 * (Phase 7) para:
 *   1. Renderizar título y mensaje localizados en cada item de la sección
 *      "No procesadas (M)" del modal de resultado.
 *   2. Subdividir esa sección en "Errores recuperables (X)" / "Errores no
 *      recuperables (M-X)" segun `isRetryable`.
 *   3. Habilitar el botón "Reintentar fallidas (X)" sólo si `X >= 1` y
 *      `retryCount < 3` (OQ-4 cerrada con opción B — Engram #60 v2).
 *
 * IMPORTANTE: este mapping debe mantenerse en sync con
 * `bff-moor/src/controllers/application/bulk-decision.response.ts`
 * (`BulkErrorCode` union) y con el tipo `BulkErrorCode` re-exportado desde
 * `src/services/ApplicationService.tsx`. Si el BFF agrega un código nuevo,
 * sumarlo acá; si lo renombra, actualizar el mapping. Phase 7 consume
 * `getErrorCopy` desde `CommitteeBulkResultModal.tsx`.
 *
 * Linaje SDD: Engram #59 (design — sección 3.6) + #60 (tasks v2 OQ-4) +
 * #63/#64/#69 (BFF foundation/core/fix final).
 */

import type { BulkErrorCode } from '../services/ApplicationService';

export interface BulkErrorCopy {
  /** Título corto para el header del item dentro de la sección. */
  title: string;
  /** Mensaje en español, full sentence, ya formateado para mostrar al usuario. */
  message: string;
  /**
   * `true` si el error puede reintentarse automáticamente con el mismo
   * payload (`action` + `description`). El modal de resultado usa este flag
   * para subdividir la sección "No procesadas" y para habilitar el botón
   * "Reintentar fallidas".
   */
  isRetryable: boolean;
}

const COPY_BY_CODE: Record<BulkErrorCode, BulkErrorCopy> = {
  STATUS_NOT_COM: {
    title: 'Estado cambió',
    message: 'La carpeta ya no está en comité (otro usuario la movió).',
    isRetryable: false,
  },
  FORBIDDEN_ROLE: {
    title: 'Sin permiso',
    message: 'Tu rol no puede aprobar/declinar esta carpeta.',
    isRetryable: false,
  },
  NOT_AUTHORIZED_FOR_STEP: {
    title: 'No es tu turno',
    message: 'Tu rol no corresponde al step actual del comité.',
    isRetryable: false,
  },
  LOCK_TIMEOUT: {
    title: 'Conflicto de concurrencia',
    message: 'Otro miembro está procesando esta carpeta. Reintentá en unos segundos.',
    isRetryable: true,
  },
  UPSTREAM_ERROR: {
    title: 'Error del servicio',
    message: 'El microservicio no respondió correctamente. Reintentá.',
    isRetryable: true,
  },
  VALIDATION_ERROR: {
    title: 'Datos inválidos',
    message: 'La acción no se puede aplicar a esta carpeta (ej. REJ no soportado en modo quórum).',
    isRetryable: false,
  },
  UNKNOWN: {
    title: 'Error desconocido',
    message: 'No se pudo procesar la carpeta. Contactá al soporte si persiste.',
    isRetryable: true,
  },
};

/**
 * Devuelve la copy en español para un `BulkErrorCode` dado.
 *
 * Si el código no está mapeado (ej. el BFF agrega uno nuevo en una versión
 * futura sin que el front se actualice), devuelve la copy de `UNKNOWN` como
 * fallback seguro: `isRetryable: true` para que el usuario tenga la opción
 * de intentar de nuevo y el flujo no quede bloqueado.
 *
 * El parámetro acepta `BulkErrorCode | string` para tolerar respuestas
 * inesperadas sin romper el render del modal — el cast interno garantiza la
 * lookup tipada.
 */
export const getErrorCopy = (errorCode: BulkErrorCode | string): BulkErrorCopy => {
  return COPY_BY_CODE[errorCode as BulkErrorCode] ?? COPY_BY_CODE.UNKNOWN;
};
